<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->
<template>
  <base-widget icon="clock-fill" title="Current Month Hours">
    <div class="orangehrm-paper-container">
      <oxd-card-table
        :headers="headers"
        :items="items?.data"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
  </base-widget>
</template>

<script>
import {computed, ref} from 'vue';
import {required, validDateFormat} from '@/core/util/validation/rules';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {freshDate, formatDate} from '@ohrm/core/util/helper/datefns';
import {yearRange} from '@/core/util/helper/year-range';
import useDateFormat from '@/core/util/composable/useDateFormat';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';

export default {
  components: {
    'base-widget': BaseWidget,
  },
  props: {
    date: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    const {userDateFormat} = useDateFormat();

    const rules = {
      date: [required, validDateFormat(userDateFormat)],
    };

    const filters = ref({
      date: props.date ? props.date : formatDate(freshDate(), 'yyyy-MM-dd'),
      employee: null,
    });

    const serializedFilters = computed(() => {
      return {
        date: filters.value.date,
        empNumber: filters.value.employee?.id,
      };
    });

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        return {
          id: item.id,
          name: item.name,
          duration: item.sum?.label,
        };
      });
    };

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/dashboard/customers/timesheet',
    );
    const {
      total,
      pages,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: attendanceRecordNormalizer,
    });

    return {
      http,
      rules,
      total,
      pages,
      filters,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
    };
  },

  data() {
    return {
      yearArray: [...yearRange()],
      headers: [
        {
          name: 'name',
          slot: 'title',
          title: 'Customer Name',
          style: {flex: '70%'},
        },
        {
          name: 'duration',
          title: 'Hours',
          style: {flex: '25%'},
        },
        {
          name: 'actions',
          title: this.$t('general.actions'),
          slot: 'action',
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            edit: {
              onClick: this.onClickView,
              props: {
                name: 'eye-fill',
              },
            },
          },
        },
      ],
    };
  },

  methods: {
    onClickView(item) {
      navigate('/time/displayAllTimeReportCriteria', undefined, {
        customerId: item.id,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.card-footer-slot) {
  .oxd-table-cell-actions {
    justify-content: flex-end;
  }
  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}
</style>
